import type { CSSProperties } from 'react';
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { isEmpty, isNull } from 'lodash';

import { useChatContext } from '~/components/AiChat/hooks/useChat';
import { ChatContextDocumentsDialog } from '~/components/AiChat/SideBarDialog/ChatContextDocumentsDialog/ChatContextDocumentsDialog';
import { useClaim } from '~/components/ClaimContainer';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import InputBase from '~/components/core/Atomic/InputBase';
import ListItemText from '~/components/core/Atomic/ListItemText';
import Menu from '~/components/core/Atomic/Menu';
import MenuItem from '~/components/core/Atomic/MenuItem';
import FsMenuItem from '~/components/core/FsWrappers/FsMenuItem/FsMenuItem';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { RestrictedPermissions } from '~/components/core/Permissions/RestrictedPermissions';
import type { DocumentFile } from '~/components/Documents/UploadDocumentsCard/UploadAIDocumentsCard';
import MultiUploadDocuments from '~/components/Documents/UploadDocumentsCard/UploadAIDocumentsCard';
import UploadClaimDocumentsWithClive from '~/components/Documents/UploadDocumentsCard/UploadClaimDocumentsWithClive';
import { FileIcon, PlusIcon, UploadIcon } from '~/components/icons';
import SendMessageIcon from '~/components/icons/SendMessageIcon';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';
import Divider from '~/components/ui/atoms/Divider';
import { reportAxiosError } from '~/Utils';
import { getSubOrganizationId } from '~/Utils/ClaimUtils';

export interface ChatInputFieldProps {
  onUserInput: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onUserSubmit: () => Promise<void>;
  onPromptSubmit?: (promptTitle: string, promptKey: string) => Promise<void>;
  disabled: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  value: string;
  type: 'elevated' | 'inline';
}

const typeToStyleProps: Record<ChatInputFieldProps['type'], { className: string; style: CSSProperties }> = {
  elevated: {
    className: 'flex w-full items-center rounded-2xl border-2 border-solid border-[#1B2559] py-12 pl-20 pr-12',
    style: {
      boxShadow: 'rgb(78 97 236 / 16%) 0px 12px 20px 0px',
    },
  },
  inline: {
    className: 'flex w-full items-center py-12 pl-20 pr-12',
    style: {},
  },
};

export const ChatInputField: React.FC<ChatInputFieldProps> = ({
  onUserInput,
  onUserSubmit,
  onPromptSubmit,
  disabled,
  inputRef,
  value,
  type = 'inline',
}) => {
  const { claim } = useClaim({ claimRequired: true });
  const { chatDocumentIds = [], setChatDocumentIds, predefinedPrompts = {} } = useChatContext();
  const { isCliveEnabled } = useCliveClaimContext();
  const subOrganizationId = getSubOrganizationId(claim);
  const isCliveDocumentsUploadEnabled = isCliveEnabled(subOrganizationId);
  const [newDocumentOpen, setNewDocumentOpen] = useState(false);
  const [documentSelectionOpen, setDocumentsSelectionOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = !isNull(menuAnchorEl);

  const sortedPrompts = useMemo(() => {
    return Object.entries(predefinedPrompts).sort(([, a], [, b]) => a.prompt_title.localeCompare(b.prompt_title));
  }, [predefinedPrompts]);

  const onAddDocumentsToChat = (selectedIds: number[]) => {
    setChatDocumentIds?.(selectedIds);
    setDocumentsSelectionOpen(false);
  };

  const handlePromptSelect = async (promptKey: string, promptTitle: string) => {
    setMenuAnchorEl(null);

    if (onPromptSubmit) {
      await onPromptSubmit(promptTitle, promptKey);
    }
  };

  const renderPredefinedPrompts = () => {
    if (isEmpty(predefinedPrompts)) return null;

    return [
      <Divider key="divider" />,
      ...sortedPrompts.map(([promptKey, promptData]) => (
        <MenuItem key={promptKey} onClick={() => handlePromptSelect(promptKey, promptData.prompt_title)}>
          <ListItemText primary={promptData.prompt_title} />
        </MenuItem>
      )),
    ];
  };

  return (
    <div {...typeToStyleProps[type]}>
      <IconButton
        onClick={(event) => setMenuAnchorEl(event.currentTarget)}
        disabled={disabled}
        aria-label="Open menu"
        className=""
      >
        <PlusIcon iconColor="black" />
      </IconButton>

      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={() => setMenuAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          style: { minWidth: '280px', maxHeight: '400px' },
        }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            setDocumentsSelectionOpen(true);
          }}
        >
          <FileIcon iconColor="gray" size={16} className="mr-8" />
          <ListItemText primary="Add context" />
        </MenuItem>

        <RestrictedPermissions action={PERMISSION_ACTIONS.CLAIM_DOCUMENT} verb={PERMISSION_VERBS.WRITE}>
          <FsMenuItem
            onClick={() => {
              setMenuAnchorEl(null);
              setNewDocumentOpen(true);
            }}
          >
            <UploadIcon iconColor="gray" size={16} className="mr-8" />
            <ListItemText primary="Upload file" />
          </FsMenuItem>
        </RestrictedPermissions>

        {renderPredefinedPrompts()}
      </Menu>

      <InputBase
        inputRef={inputRef}
        autoFocus
        fullWidth
        value={value}
        placeholder="Ask me anything"
        onChange={onUserInput}
        disabled={disabled}
        multiline
        onKeyDown={async (e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            await onUserSubmit();
          }
        }}
      />

      <IconButton className="bg-pink-600 p-12 hover:bg-pink-300" onClick={onUserSubmit} disabled={disabled}>
        <SendMessageIcon iconColor="white" size={20} />
      </IconButton>
      {newDocumentOpen &&
        (isCliveDocumentsUploadEnabled ? (
          <UploadClaimDocumentsWithClive
            claim={claim}
            onCancel={() => setNewDocumentOpen(false)}
            onSubmitDocument={() => {
              // Not used for Clive flow, but required by interface
            }}
            onCliveUploadSuccess={() => setNewDocumentOpen(false)}
            forceCliveEnabled={true}
          />
        ) : (
          <MultiUploadDocuments
            claim={claim}
            onSubmitDocument={async (documents: DocumentFile[]) => {
              try {
                await axios.post(`/api/v1/claims/${claim.id}/ai/auto_doc_upload`, {
                  stored_file_ids: documents.map((doc) => doc.stored_file_id),
                });
                setNewDocumentOpen(false);
              } catch (error) {
                await reportAxiosError(error);
              }
            }}
            onCancel={() => setNewDocumentOpen(false)}
          />
        ))}
      {documentSelectionOpen ? (
        <ChatContextDocumentsDialog
          open={documentSelectionOpen}
          onClose={() => setDocumentsSelectionOpen(false)}
          onAddToChat={(selectedIds) => onAddDocumentsToChat(selectedIds)}
          documentIds={chatDocumentIds}
        />
      ) : null}
    </div>
  );
};
